import React, { useState, useEffect } from 'react';
import FollowUpService from '../services/followUpService';
import { LeadsService } from '../services/leadsService';
import { leadsApi } from '../config/api';

const FollowUpManager = ({ leads: propLeads, dashboardFollowUps, onUpdateLead }) => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [allLeads, setAllLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  const [followUpType, setFollowUpType] = useState('call');
  const [followUpDate, setFollowUpDate] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  const [summary, setSummary] = useState('');
  const [notes, setNotes] = useState('');
  const [priority, setPriority] = useState('medium');
  const [leadSearch, setLeadSearch] = useState('');
  const [showLeadDropdown, setShowLeadDropdown] = useState(false);
  const [visibleHistoryIds, setVisibleHistoryIds] = useState(new Set());
  
  const [rescheduleModal, setRescheduleModal] = useState({ isOpen: false, followUp: null });
  const [rescheduleData, setRescheduleData] = useState({ newDate: '', reason: '' });
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  useEffect(() => {
    loadFollowUps();
    loadAllLeads();
  }, []);

  const loadAllLeads = async () => {
    try {
      if (propLeads && propLeads.length > 0) {
        setAllLeads(propLeads);
      } else {
        const result = await LeadsService.getAllLeads();
        if (result.success && result.data) setAllLeads(result.data);
      }
    } catch (e) {
      console.error('Error loading leads:', e);
    }
  };

  const loadFollowUps = async () => {
    setLoading(true);
    try {
      const upcomingResult = await FollowUpService.getUpcomingFollowUps();
      const overdueResult = await FollowUpService.getOverdueFollowUps();
      let all = [];
      if (dashboardFollowUps && dashboardFollowUps.length > 0) all = [...dashboardFollowUps];
      if (upcomingResult.success && upcomingResult.data) all = [...all, ...upcomingResult.data];
      if (overdueResult.success && overdueResult.data) all = [...all, ...overdueResult.data];

      const normalized = all.map(f => {
        const id = f._id || f.id || f.followUpId;
        if (f.followUp && typeof f.followUp === 'object') {
          return {
            ...f.followUp,
            _id: f.followUp._id || id,
            id: f.followUp._id || id,
            leadId: f.leadId || f._id,
            leadName: f.leadName || f.name,
            leadCompany: typeof f.leadCompany === 'string' ? f.leadCompany : (f.leadCompany?.name || f.company || ''),
            scheduledDate: f.followUp.nextFollowUpDate || f.followUp.scheduledDate || f.followUp.date,
            rescheduled: f.followUp.rescheduled || false,
            rescheduleHistory: f.followUp.rescheduleHistory || []
          };
        }
        return {
          ...f,
          _id: id,
          id: id,
          leadCompany: typeof f.leadCompany === 'string' ? f.leadCompany : (f.leadCompany?.name || f.company || ''),
          scheduledDate: f.nextFollowUpDate || f.scheduledDate || f.date,
          rescheduled: f.rescheduled || false,
          rescheduleHistory: f.rescheduleHistory || []
        };
      });

      const unique = normalized.reduce((acc, cur) => {
        const cid = cur._id || cur.id;
        if (!cid || acc.find(x => (x._id || x.id) === cid)) return acc;
        acc.push(cur);
        return acc;
      }, []);

      try {
        const leadsResult = await LeadsService.getAllLeads();
        if (leadsResult.success && leadsResult.data) {
          leadsResult.data.forEach(lead => {
            if (lead.followUps && lead.followUps.length > 0) {
              lead.followUps.forEach(fu => {
                const fid = fu._id || fu.id;
                if (!fid || unique.find(x => (x._id || x.id) === fid)) return;
                unique.push({
                  ...fu,
                  _id: fid,
                  id: fid,
                  leadId: lead._id || lead.id,
                  leadName: lead.name,
                  leadCompany: typeof lead.company === 'string' ? lead.company : (lead.company?.name || ''),
                  scheduledDate: fu.nextFollowUpDate || fu.scheduledDate || fu.date,
                  rescheduled: fu.rescheduled || false,
                  rescheduleHistory: fu.rescheduleHistory || []
                });
              });
            }
          });
        }
      } catch (e) {}

      setFollowUps(unique);
    } catch (e) {
      console.error('Error loading follow-ups:', e);
    } finally {
      setLoading(false);
    }
  };

  const addFollowUp = async () => {
    if (!selectedLead || !followUpDate || !followUpTime) {
      alert('Please fill all required fields');
      return;
    }
    const lead = allLeads.find(l => (l._id || l.id) === selectedLead);
    if (!lead) { alert('Lead not found'); return; }

    let currentUser = null;
    try {
      const storedUser = localStorage.getItem('userInfo') || localStorage.getItem('userData');
      if (storedUser) currentUser = JSON.parse(storedUser);
    } catch (e) {
      console.error('Error parsing user info:', e);
    }

    const localDate = new Date(`${followUpDate}T${followUpTime}`);
    const combinedDate = !isNaN(localDate.getTime()) ? localDate.toISOString() : `${followUpDate}T${followUpTime}`;

    const result = await FollowUpService.addFollowUp(lead._id || lead.id, {
      type: followUpType,
      summary: summary.trim() || notes.trim() || `${followUpType.charAt(0).toUpperCase() + followUpType.slice(1)} Follow-up`,
      notes,
      nextFollowUpDate: combinedDate,
      scheduledDate: combinedDate,
      priority,
      userId: currentUser?.id || currentUser?._id
    });
    if (result.success) {
      await loadFollowUps();
      setShowAddForm(false);
      setSelectedLead('');
      setFollowUpDate('');
      setFollowUpTime('');
      setSummary('');
      setNotes('');
    } else {
      alert(result.message || 'Failed to add follow-up');
    }
  };

  const completeFollowUp = async (fu) => {
    const lid = fu.leadId;
    const fid = fu._id || fu.id;
    if (!lid || !fid) return;
    await FollowUpService.updateFollowUp(lid, fid, { status: 'completed' });
    await loadFollowUps();
  };

  const deleteFollowUp = async (fu) => {
    if (!window.confirm('Delete this follow-up?')) return;
    const lid = fu.leadId;
    const fid = fu._id || fu.id;
    if (!lid || !fid) return;
    await FollowUpService.deleteFollowUp(lid, fid);
    await loadFollowUps();
  };

  const toggleHistory = (id) => {
    setVisibleHistoryIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const getCurrentUser = () => {
    try {
      const storedUser = localStorage.getItem('userInfo') || localStorage.getItem('userData');
      if (storedUser) return JSON.parse(storedUser);
      
      const token = localStorage.getItem('authToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          id: payload.id,
          email: payload.email,
          role: payload.role,
          name: payload.name || payload.email?.split('@')[0]
        };
      }
    } catch (e) {
      console.error('Error getting user:', e);
    }
    return null;
  };

  const getCurrentUserName = () => {
    const user = getCurrentUser();
    if (!user) return 'Unknown';
    return user.businessName || user.name || user.teamMemberName || user.email?.split('@')[0] || 'User';
  };

  const getCurrentUserEmail = () => {
    const user = getCurrentUser();
    return user?.email || user?.businessEmail || '';
  };

  const openRescheduleModal = (followUp) => {
    const currentDate = followUp.nextFollowUpDate || followUp.scheduledDate || followUp.date;
    setRescheduleData({
      newDate: currentDate ? new Date(currentDate).toISOString().slice(0, 16) : '',
      reason: ''
    });
    setRescheduleModal({ isOpen: true, followUp });
  };

  const handleReschedule = async () => {
    if (!rescheduleData.newDate) {
      alert('Please select a new date');
      return;
    }
    if (!rescheduleData.reason.trim()) {
      alert('Please provide a reason for rescheduling');
      return;
    }
    
    setRescheduleLoading(true);
    try {
      const followUp = rescheduleModal.followUp;
      const leadId = followUp.leadId;
      const followUpId = followUp._id || followUp.id;
      const previousDate = followUp.nextFollowUpDate || followUp.scheduledDate || followUp.date;
      
      const userName = getCurrentUserName();
      const userEmail = getCurrentUserEmail();
      
      const newHistoryEntry = {
        previousDate: previousDate,
        newDate: new Date(rescheduleData.newDate).toISOString(),
        reason: rescheduleData.reason.trim(),
        rescheduledAt: new Date().toISOString(),
        rescheduledBy: userName,
        rescheduledByEmail: userEmail
      };
      
      const existingHistory = followUp.rescheduleHistory || [];
      const updatedHistory = [...existingHistory, newHistoryEntry];
      
      await leadsApi.put(`/follow-up/update/${leadId}/${followUpId}`, {
        scheduledDate: new Date(rescheduleData.newDate).toISOString(),
        nextFollowUpDate: new Date(rescheduleData.newDate).toISOString(),
        rescheduled: true,
        rescheduledAt: new Date().toISOString(),
        rescheduledFrom: previousDate,
        rescheduleHistory: updatedHistory
      });
      
      await loadFollowUps();
      setRescheduleModal({ isOpen: false, followUp: null });
      setRescheduleData({ newDate: '', reason: '' });
      alert('Follow-up rescheduled successfully');
    } catch (err) {
      console.error('Error rescheduling:', err);
      alert('Failed to reschedule follow-up');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const isOverdue = (date) => date && new Date(date) < new Date() && new Date(date).toDateString() !== new Date().toDateString();

  const now = new Date();
  const filtered = followUps.filter(f => {
    if (filter === 'pending') return f.status !== 'completed' && !f.rescheduled;
    if (filter === 'completed') return f.status === 'completed';
    if (filter === 'overdue') return f.status !== 'completed' && isOverdue(f.scheduledDate);
    if (filter === 'rescheduled') return f.rescheduled === true && f.status !== 'completed';
    if (filter === 'today') return f.scheduledDate && new Date(f.scheduledDate).toDateString() === now.toDateString();
    return true;
  }).sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

  const pendingCount = followUps.filter(f => f.status !== 'completed' && !f.rescheduled).length;
  const overdueCount = followUps.filter(f => f.status !== 'completed' && isOverdue(f.scheduledDate)).length;
  const rescheduledCount = followUps.filter(f => f.rescheduled && f.status !== 'completed').length;
  const todayCount = followUps.filter(f => f.scheduledDate && new Date(f.scheduledDate).toDateString() === now.toDateString()).length;

  const typeIcon = t => ({ call: '📞', email: '📧', meeting: '🤝', demo: '💻', proposal: '📋' }[t] || '📝');

  const fmtDate = d => d && !isNaN(new Date(d)) ? new Date(d).toLocaleDateString('en-IN') : 'No Date';
  const fmtTime = d => d && !isNaN(new Date(d)) ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

  const filteredLeads = allLeads.filter(l => {
    const q = leadSearch.toLowerCase();
    return !q || (l.name || '').toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q) || (l.phone || '').includes(q);
  });

  const selectedLeadInfo = allLeads.find(l => (l._id || l.id) === selectedLead);

  /* ─── inline styles ─────────────────────────────────────────── */
  const S = {
    // containers
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif"
    },
    card: {
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      padding: '24px'
    },
    // header
    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '16px'
    },
    title: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#1a202c',
      margin: '0 0 4px 0',
      letterSpacing: '-0.3px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    titleAccent: {
      display: 'inline-block',
      width: '14px',
      height: '14px',
      borderRadius: '3px',
      background: '#2563eb',
      flexShrink: 0
    },
    subtitle: {
      fontSize: '13px',
      color: '#64748b',
      margin: 0
    },
    scheduleBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '7px',
      background: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      letterSpacing: '0.1px',
      transition: 'background 0.18s'
    },
    // stats grid
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
      gap: '12px',
      marginTop: '20px'
    },
    statCard: (borderColor) => ({
      background: '#f8fafc',
      border: `1px solid #e2e8f0`,
      borderTop: `3px solid ${borderColor}`,
      borderRadius: '10px',
      padding: '16px 12px',
      textAlign: 'center'
    }),
    statNum: {
      fontSize: '26px',
      fontWeight: '700',
      color: '#111827',
      lineHeight: 1,
      margin: '0 0 6px 0'
    },
    statLabel: {
      fontSize: '12px',
      fontWeight: '500',
      color: '#64748b',
      margin: 0,
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    // form
    formTitle: {
      fontSize: '16px',
      fontWeight: '700',
      color: '#1a202c',
      margin: '0 0 18px 0'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: '16px'
    },
    label: {
      display: 'block',
      fontSize: '12px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.4px'
    },
    input: {
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#111827',
      background: '#fff',
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#111827',
      background: '#fff',
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      cursor: 'pointer'
    },
    textarea: {
      width: '100%',
      padding: '10px 14px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#111827',
      background: '#fff',
      outline: 'none',
      resize: 'vertical',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      minHeight: '80px'
    },
    formActions: {
      display: 'flex',
      gap: '10px',
      marginTop: '18px'
    },
    btnPrimary: {
      background: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 22px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: 'inherit'
    },
    btnSecondary: {
      background: '#f1f5f9',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: '10px 22px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: 'inherit'
    },
    // filter bar
    filterCard: {
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      padding: '16px 20px'
    },
    filterTitle: {
      fontSize: '12px',
      fontWeight: '700',
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      margin: '0 0 12px 0'
    },
    filterRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px'
    },
    filterBtn: (active) => ({
      padding: '7px 16px',
      borderRadius: '7px',
      border: active ? '1.5px solid #2563eb' : '1px solid #d1d5db',
      background: active ? '#2563eb' : '#f8fafc',
      color: active ? '#fff' : '#374151',
      fontSize: '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.15s',
      fontFamily: 'inherit'
    }),
    // table
    tableCard: {
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
      overflow: 'hidden'
    },
    tableHeader: {
      padding: '14px 20px',
      borderBottom: '1px solid #e2e8f0',
      background: '#f8fafc',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    tableHeaderTitle: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#1a202c',
      margin: 0
    },
    tableHeaderCount: {
      fontSize: '12px',
      color: '#64748b',
      background: '#e2e8f0',
      padding: '2px 10px',
      borderRadius: '12px',
      fontWeight: '600'
    },
    th: {
      padding: '11px 18px',
      fontSize: '11px',
      fontWeight: '700',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.6px',
      borderBottom: '1px solid #e2e8f0',
      background: '#f8fafc',
      textAlign: 'left',
      whiteSpace: 'nowrap'
    },
    td: {
      padding: '14px 18px',
      borderBottom: '1px solid #f1f5f9',
      verticalAlign: 'middle'
    },
    leadName: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#111827',
      margin: '0 0 2px 0'
    },
    leadCompany: {
      fontSize: '12px',
      color: '#6b7280',
      margin: '0 0 2px 0'
    },
    leadNotes: {
      fontSize: '11px',
      color: '#9ca3af',
      maxWidth: '180px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    typeCell: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      color: '#374151',
      fontWeight: '500',
      textTransform: 'capitalize'
    },
    scheduleDate: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#111827',
      margin: '0 0 2px 0'
    },
    scheduleTime: {
      fontSize: '11px',
      color: '#6b7280',
      margin: 0
    },
    badge: (bg, color) => ({
      display: 'inline-flex',
      alignItems: 'center',
      padding: '3px 10px',
      borderRadius: '5px',
      fontSize: '11px',
      fontWeight: '700',
      background: bg,
      color: color,
      letterSpacing: '0.3px'
    }),
    actionComplete: {
      padding: '5px 12px',
      background: '#f0fdf4',
      color: '#15803d',
      border: '1px solid #bbf7d0',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: 'inherit'
    },
    actionDelete: {
      padding: '5px 12px',
      background: '#fef2f2',
      color: '#dc2626',
      border: '1px solid #fecaca',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: 'inherit'
    },
    actionReschedule: {
      padding: '5px 12px',
      background: '#eff6ff',
      color: '#2563eb',
      border: '1px solid #bfdbfe',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      fontFamily: 'inherit'
    },
    historyRow: {
      background: '#f0f7ff',
      borderBottom: '1px solid #e2e8f0'
    },
    historyItem: {
      fontSize: '12px',
      color: '#374151',
      margin: '0 0 4px 0'
    },
    emptyBox: {
      padding: '60px 20px',
      textAlign: 'center'
    },
    emptyIcon: {
      fontSize: '40px',
      marginBottom: '12px'
    },
    emptyTitle: {
      fontSize: '15px',
      fontWeight: '700',
      color: '#374151',
      margin: '0 0 6px 0'
    },
    emptyText: {
      fontSize: '13px',
      color: '#9ca3af',
      margin: 0
    },
    spinner: {
      display: 'inline-block',
      width: '28px',
      height: '28px',
      border: '3px solid #e2e8f0',
      borderTopColor: '#2563eb',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      marginBottom: '12px'
    }
  };

  const statItems = [
    { label: 'My Follow-ups', count: followUps.length, color: '#2563eb' },
    { label: 'Pending',        count: pendingCount,     color: '#2563eb' },
    { label: 'Rescheduled',    count: rescheduledCount, color: '#2563eb' },
    { label: 'Overdue',        count: overdueCount,     color: '#2563eb' },
    { label: 'Today',          count: todayCount,       color: '#2563eb' }
  ];

  const filterOpts = [
    { key: 'all',        label: 'All My Follow-ups',  count: followUps.length },
    { key: 'pending',    label: 'Pending',             count: pendingCount },
    { key: 'rescheduled',label: 'Rescheduled',         count: rescheduledCount },
    { key: 'today',      label: 'Today',               count: todayCount },
    { key: 'overdue',    label: 'Overdue',             count: overdueCount },
    { key: 'completed',  label: 'Completed',           count: followUps.filter(f => f.status === 'completed').length }
  ];

  const priorityBadge = (p) => {
    const map = {
      high:   { bg: '#fef2f2', color: '#dc2626' },
      medium: { bg: '#fffbeb', color: '#b45309' },
      low:    { bg: '#f0fdf4', color: '#15803d' }
    };
    return map[p] || map.medium;
  };

  return (
    <div style={S.wrapper} className="fum-wrapper">
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        @media (max-width: 768px) {
          /* ── Wrapper ── */
          .fum-wrapper {
            gap: 14px !important;
            padding: 0 2px !important;
          }

          /* ── Header card ── */
          .fum-header-card {
            padding: 16px !important;
          }
          .fum-header-row {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .fum-title {
            font-size: 17px !important;
          }
          .fum-subtitle {
            font-size: 12px !important;
          }
          .fum-schedule-btn {
            width: 100% !important;
            justify-content: center !important;
            padding: 11px 16px !important;
            font-size: 14px !important;
            border-radius: 8px !important;
          }

          /* ── Stats grid – 2 columns on mobile ── */
          .fum-stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
            margin-top: 14px !important;
          }
          .fum-stat-card {
            padding: 14px 10px !important;
            border-radius: 9px !important;
          }
          .fum-stat-num {
            font-size: 22px !important;
            margin-bottom: 4px !important;
          }
          .fum-stat-label {
            font-size: 10px !important;
            letter-spacing: 0.3px !important;
          }

          /* ── Filter card ── */
          .fum-filter-card {
            padding: 14px 14px !important;
          }
          .fum-filter-title {
            font-size: 11px !important;
            margin-bottom: 10px !important;
          }
          .fum-filter-row {
            display: grid !important;
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 8px !important;
          }
          .fum-filter-btn {
            width: 100% !important;
            text-align: center !important;
            padding: 9px 6px !important;
            font-size: 12px !important;
            border-radius: 7px !important;
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }

          /* ── Add form ── */
          .fum-form-card {
            padding: 16px !important;
          }
          .fum-form-grid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .fum-form-actions {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .fum-btn-primary,
          .fum-btn-secondary {
            width: 100% !important;
            text-align: center !important;
            padding: 11px 16px !important;
            font-size: 14px !important;
          }

          /* ── Table card ── */
          .fum-table-card {
            border-radius: 10px !important;
            overflow: hidden !important;
          }
          .fum-table-header {
            padding: 12px 14px !important;
          }
          .fum-table-header-title {
            font-size: 14px !important;
            font-weight: 700 !important;
            color: #111827 !important;
          }
          .fum-table-header-count {
            font-size: 12px !important;
            font-weight: 600 !important;
            color: #374151 !important;
          }

          /* Stack table rows into card-like blocks on mobile */
          .fum-table-wrap table,
          .fum-table-wrap thead,
          .fum-table-wrap tbody,
          .fum-table-wrap th,
          .fum-table-wrap td,
          .fum-table-wrap tr {
            display: block !important;
          }
          .fum-table-wrap thead tr {
            display: none !important;
          }
          .fum-table-wrap tbody tr {
            margin: 10px !important;
            border: 1px solid #d1d5db !important;
            border-left: 3px solid #2563eb !important;
            border-radius: 10px !important;
            padding: 4px 0 !important;
            background: #ffffff !important;
            box-shadow: 0 1px 4px rgba(0,0,0,0.06) !important;
          }
          .fum-table-wrap td {
            padding: 9px 14px !important;
            border: none !important;
            border-bottom: 1px solid #f1f5f9 !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            color: #111827 !important;
            display: flex !important;
            align-items: center !important;
            gap: 10px !important;
            min-height: 38px !important;
          }
          .fum-table-wrap td:last-child {
            border-bottom: none !important;
          }
          .fum-table-wrap td::before {
            content: attr(data-label);
            min-width: 90px !important;
            font-size: 11px !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.6px !important;
            color: #374151 !important;
            flex-shrink: 0 !important;
          }
          .fum-lead-name {
            font-size: 14px !important;
            font-weight: 700 !important;
            color: #111827 !important;
            margin: 0 !important;
          }
          .fum-lead-company {
            font-size: 12px !important;
            font-weight: 500 !important;
            color: #4b5563 !important;
            margin: 0 !important;
          }
          .fum-action-complete,
          .fum-action-reschedule,
          .fum-action-delete {
            font-size: 12px !important;
            font-weight: 600 !important;
            padding: 6px 12px !important;
            border-radius: 6px !important;
          }
          .fum-table-wrap td[data-label="Actions"] {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 6px !important;
          }
          .fum-table-wrap td[data-label="Actions"]::before {
            margin-bottom: 2px !important;
          }
        }
      `}</style>

      {/* ── Header Card ── */}
      <div style={S.card} className="fum-header-card">
        <div style={S.headerRow} className="fum-header-row">
          <div>
            <h2 style={S.title} className="fum-title">
              <span style={S.titleAccent}></span>
              Follow-Up Manager
            </h2>
            <p style={S.subtitle} className="fum-subtitle">Schedule and track your lead follow-ups</p>
          </div>
          <button
            style={S.scheduleBtn}
            className="fum-schedule-btn"
            onMouseOver={e => e.currentTarget.style.background = '#1d4ed8'}
            onMouseOut={e => e.currentTarget.style.background = '#2563eb'}
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <span style={{ fontSize: '15px' }}>+</span> Schedule Follow-Up
          </button>
        </div>

        {/* Stats */}
        <div style={S.statsGrid} className="fum-stats-grid">
          {statItems.map(s => (
            <div key={s.label} style={S.statCard(s.color)} className="fum-stat-card">
              <p style={S.statNum} className="fum-stat-num">{s.count}</p>
              <p style={S.statLabel} className="fum-stat-label">{s.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Add Form ── */}
      {showAddForm && (
        <div style={S.card} className="fum-form-card">
          <h3 style={S.formTitle}>Schedule New Follow-Up</h3>
          <div style={S.formGrid} className="fum-form-grid">
            {/* Lead Select */}
            <div>
              <label style={S.label}>Select Lead *</label>
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setShowLeadDropdown(!showLeadDropdown)}
                  style={{ ...S.input, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                >
                  <span style={{ color: selectedLeadInfo ? '#111827' : '#9ca3af' }}>
                    {selectedLeadInfo ? selectedLeadInfo.name : 'Choose a lead...'}
                  </span>
                  <span style={{ color: '#9ca3af', fontSize: '11px' }}>{showLeadDropdown ? '▲' : '▼'}</span>
                </button>
                {showLeadDropdown && (
                  <div style={{ position: 'absolute', zIndex: 50, width: '100%', marginTop: '4px', background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: '280px', overflow: 'hidden' }}>
                    <div style={{ padding: '10px', borderBottom: '1px solid #e2e8f0' }}>
                      <input type="text" placeholder="Search leads..." value={leadSearch} onChange={e => setLeadSearch(e.target.value)} style={{ ...S.input, fontSize: '13px' }} autoFocus />
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {filteredLeads.map(l => (
                        <div
                          key={l._id || l.id}
                          onClick={() => { setSelectedLead(l._id || l.id); setShowLeadDropdown(false); setLeadSearch(''); }}
                          style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: (l._id || l.id) === selectedLead ? '#eff6ff' : '#fff' }}
                          onMouseOver={e => e.currentTarget.style.background = '#eff6ff'}
                          onMouseOut={e => e.currentTarget.style.background = (l._id || l.id) === selectedLead ? '#eff6ff' : '#fff'}
                        >
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>{l.name}</div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>{l.company} · {l.email}</div>
                        </div>
                      ))}
                      {filteredLeads.length === 0 && <div style={{ padding: '16px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No leads found</div>}
                    </div>
                    <div style={{ padding: '8px', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
                      <button onClick={() => { setShowLeadDropdown(false); setLeadSearch(''); }} style={{ fontSize: '12px', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>Close</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Type */}
            <div>
              <label style={S.label}>Type</label>
              <select value={followUpType} onChange={e => setFollowUpType(e.target.value)} style={S.select}>
                <option value="call">📞 Phone Call</option>
                <option value="email">📧 Email</option>
                <option value="meeting">🤝 Meeting</option>
                <option value="demo">💻 Demo</option>
                <option value="proposal">📋 Proposal</option>
              </select>
            </div>

            {/* Date */}
            <div>
              <label style={S.label}>Date *</label>
              <input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} min={new Date().toISOString().split('T')[0]} style={S.input} />
            </div>

            {/* Time */}
            <div>
              <label style={S.label}>Time *</label>
              <input type="time" value={followUpTime} onChange={e => setFollowUpTime(e.target.value)} style={S.input} />
            </div>

            {/* Priority */}
            <div>
              <label style={S.label}>Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value)} style={S.select}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            {/* Summary */}
            <div>
              <label style={S.label}>Summary</label>
              <input type="text" value={summary} onChange={e => setSummary(e.target.value)} placeholder="Brief summary..." style={S.input} />
            </div>

            {/* Notes */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={S.label}>Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add notes..." style={S.textarea} />
            </div>
          </div>

          <div style={S.formActions} className="fum-form-actions">
            <button style={S.btnPrimary} className="fum-btn-primary" onMouseOver={e => e.currentTarget.style.background='#1d4ed8'} onMouseOut={e => e.currentTarget.style.background='#2563eb'} onClick={addFollowUp}>
              Schedule
            </button>
            <button style={S.btnSecondary} className="fum-btn-secondary" onClick={() => setShowAddForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Filter Bar ── */}
      <div style={S.filterCard} className="fum-filter-card">
        <p style={S.filterTitle} className="fum-filter-title">All My Follow-ups</p>
        <div style={S.filterRow} className="fum-filter-row">
          {filterOpts.map(opt => (
            <button
              key={opt.key}
              onClick={() => setFilter(opt.key)}
              style={S.filterBtn(filter === opt.key)}
              className="fum-filter-btn"
              onMouseOver={e => { if (filter !== opt.key) e.currentTarget.style.background = '#eff6ff'; }}
              onMouseOut={e => { if (filter !== opt.key) e.currentTarget.style.background = '#f8fafc'; }}
            >
              {opt.label} ({opt.count})
            </button>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={S.tableCard} className="fum-table-card">
        <div style={S.tableHeader} className="fum-table-header">
          <p style={S.tableHeaderTitle} className="fum-table-header-title">Follow-Up Records</p>
          <span style={S.tableHeaderCount} className="fum-table-header-count">{filtered.length} records</span>
        </div>

        {loading ? (
          <div style={S.emptyBox}>
            <div style={S.spinner}></div>
            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Loading follow-ups...</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }} className="fum-table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Lead Info', 'Type', 'Scheduled', 'Priority', 'Status', 'Actions'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div style={S.emptyBox}>
                        <div style={S.emptyIcon}>📋</div>
                        <p style={S.emptyTitle}>No Follow-ups Found</p>
                        <p style={S.emptyText}>{filter === 'all' ? 'Schedule your first follow-up!' : `No ${filter} follow-ups.`}</p>
                      </div>
                    </td>
                  </tr>
                ) : filtered.map((fu, idx) => {
                  const fid = fu._id || fu.id;
                  const isHistVis = visibleHistoryIds.has(fid);
                  const overdue = isOverdue(fu.scheduledDate) && fu.status !== 'completed';
                  const pb = priorityBadge(fu.priority);
                  return (
                    <React.Fragment key={`${fid}-${idx}`}>
                      <tr style={{ background: overdue ? '#fff5f5' : '#ffffff' }}
                        onMouseOver={e => e.currentTarget.style.background = overdue ? '#fff0f0' : '#f8fafc'}
                        onMouseOut={e => e.currentTarget.style.background = overdue ? '#fff5f5' : '#ffffff'}
                      >
                        {/* Lead Info */}
                        <td style={S.td} data-label="Lead">
                          <p style={S.leadName} className="fum-lead-name">{fu.leadName || '—'}</p>
                          {fu.leadCompany && typeof fu.leadCompany === 'string' && !/^[0-9a-fA-F]{24}$/.test(fu.leadCompany) && (
                            <p style={S.leadCompany} className="fum-lead-company">{fu.leadCompany}</p>
                          )}
                          {fu.notes && <p style={S.leadNotes}>{fu.notes}</p>}
                        </td>

                        {/* Type */}
                        <td style={S.td} data-label="Type">
                          <div style={S.typeCell}>
                            <span>{typeIcon(fu.type)}</span>
                            <span>{fu.type || '—'}</span>
                          </div>
                        </td>

                        {/* Schedule */}
                        <td style={S.td} data-label="Scheduled">
                          <p style={S.scheduleDate}>{fmtDate(fu.scheduledDate)}</p>
                          <p style={S.scheduleTime}>{fmtTime(fu.scheduledDate)}</p>
                        </td>

                        {/* Priority */}
                        <td style={S.td} data-label="Priority">
                          <span style={S.badge(pb.bg, pb.color)}>
                            {(fu.priority || 'medium').toUpperCase()}
                          </span>
                        </td>

                        {/* Status */}
                        <td style={S.td} data-label="Status">
                          <span style={S.badge(
                            fu.status === 'completed' ? '#f0fdf4' : overdue ? '#fef2f2' : '#fffbeb',
                            fu.status === 'completed' ? '#15803d' : overdue ? '#dc2626' : '#b45309'
                          )}>
                            {fu.status === 'completed' ? 'Completed' : overdue ? 'Overdue' : 'Pending'}
                          </span>
                          {fu.rescheduled && (
                            <div style={{ marginTop: '5px' }}>
                              <button
                                onClick={() => toggleHistory(fid)}
                                style={{ display: 'inline-block', fontSize: '11px', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', textDecoration: 'underline' }}
                              >
                                Rescheduled ({fu.rescheduleHistory?.length || 0}×)
                              </button>
                              {fu.rescheduleHistory && fu.rescheduleHistory.length > 0 && (
                                <div style={{ marginTop: '6px', padding: '6px 8px', background: '#eff6ff', borderRadius: '6px', borderLeft: '3px solid #2563eb', textAlign: 'left', maxWidth: '300px' }}>
                                  <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e40af', display: 'block', marginBottom: '2px' }}>Reason:</span>
                                  <span style={{ fontSize: '11px', color: '#1e3a8a', display: 'block', lineHeight: '1.3' }}>
                                    {fu.rescheduleHistory[fu.rescheduleHistory.length - 1].reason || 'No reason specified'}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </td>

                        {/* Actions */}
                        <td style={S.td} data-label="Actions">
                          <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                            {fu.status !== 'completed' && (
                              <button style={S.actionComplete} className="fum-action-complete" onClick={() => completeFollowUp(fu)}
                                onMouseOver={e => e.currentTarget.style.background='#dcfce7'}
                                onMouseOut={e => e.currentTarget.style.background='#f0fdf4'}
                              >Complete</button>
                            )}
                            {fu.status !== 'completed' && (
                              <button style={S.actionReschedule} className="fum-action-reschedule" onClick={() => openRescheduleModal(fu)}
                                onMouseOver={e => e.currentTarget.style.background='#dbeafe'}
                                onMouseOut={e => e.currentTarget.style.background='#eff6ff'}
                              >Reschedule</button>
                            )}
                            <button style={S.actionDelete} className="fum-action-delete" onClick={() => deleteFollowUp(fu)}
                              onMouseOver={e => e.currentTarget.style.background='#fee2e2'}
                              onMouseOut={e => e.currentTarget.style.background='#fef2f2'}
                            >Delete</button>
                          </div>
                        </td>
                      </tr>

                      {/* Reschedule History */}
                      {isHistVis && fu.rescheduleHistory && fu.rescheduleHistory.length > 0 && (
                        <tr style={S.historyRow}>
                          <td colSpan="6" style={{ padding: '12px 18px' }}>
                            <p style={{ fontSize: '11px', fontWeight: '700', color: '#2563eb', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reschedule History</p>
                            {fu.rescheduleHistory.map((h, i) => (
                              <p key={i} style={S.historyItem}>
                                {fmtDate(h.previousDate)} → {fmtDate(h.newDate)}{h.reason ? ` (${h.reason})` : ''}
                              </p>
                            ))}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reschedule Modal */}
      {rescheduleModal.isOpen && rescheduleModal.followUp && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            maxWidth: '450px',
            width: '100%',
            padding: '24px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>
                📅 Reschedule Follow-up
              </h3>
              <button
                onClick={() => setRescheduleModal({ isOpen: false, followUp: null })}
                style={{ background: 'none', border: 'none', fontSize: '20px', color: '#9ca3af', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>
            
            <p style={{ fontSize: '13px', color: '#4b5563', margin: '0 0 16px 0' }}>
              Rescheduling follow-up for <strong>{rescheduleModal.followUp.leadName}</strong>.
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                New Date and Time *
              </label>
              <input
                type="datetime-local"
                value={rescheduleData.newDate}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, newDate: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                Reason for Rescheduling *
              </label>
              <textarea
                placeholder="Please enter the reason for rescheduling..."
                value={rescheduleData.reason}
                onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
                rows="3"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'end', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setRescheduleModal({ isOpen: false, followUp: null })}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReschedule}
                disabled={rescheduleLoading || !rescheduleData.newDate || !rescheduleData.reason.trim()}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  opacity: (rescheduleLoading || !rescheduleData.newDate || !rescheduleData.reason.trim()) ? 0.6 : 1
                }}
              >
                {rescheduleLoading ? 'Rescheduling...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpManager;
